import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Loader, SectionCard, StatCard } from "@/components/PageUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Users, UserRound, DollarSign, CreditCard, Percent, LifeBuoy, Gift, PenSquare } from "lucide-react";
import { toast } from "sonner";

const apiErr = (e) => e?.response?.data?.detail || "Something went wrong";

function CompCard({ clubs, onChange }) {
  const [clubId, setClubId] = useState("");
  const [plan, setPlan] = useState("club");
  const [forever, setForever] = useState(false);
  const [months, setMonths] = useState("3");
  const [busy, setBusy] = useState(false);

  const grant = async () => {
    if (!clubId) return toast.error("Pick a club first");
    setBusy(true);
    try {
      const body = { club_id: clubId, plan, months: forever ? 0 : Math.max(1, parseInt(months, 10) || 1) };
      const r = await api.post("/admin/comp", body);
      toast.success(`${r.data.club_name} is now on the ${plan} plan${forever ? " (forever)" : ` for ${body.months} month(s)`}`);
      setClubId("");
      onChange();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  return (
    <SectionCard title="Comp a club" testid="admin-comp-card">
      <p className="text-sm text-muted-foreground mb-4">Grant a premium plan to a club for free — no Stripe or coupon needed.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Club</label>
          <Select value={clubId} onValueChange={setClubId}>
            <SelectTrigger className="mt-1" data-testid="comp-club-select"><SelectValue placeholder="Select a club" /></SelectTrigger>
            <SelectContent>
              {clubs.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`comp-club-option-${c.id}`}>
                  {c.name} · {c.plan}{c.comped ? " (comped)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Plan</label>
          <Select value={plan} onValueChange={setPlan}>
            <SelectTrigger className="mt-1" data-testid="comp-plan-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="starter">Starter</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
              <SelectItem value="club">Club</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4 mt-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="comp-forever-toggle">
          <input type="checkbox" checked={forever} onChange={(e) => setForever(e.target.checked)} className="h-4 w-4 rounded border-input accent-primary" />
          Forever (until revoked)
        </label>
        {!forever && (
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">Duration (months)</label>
            <Input type="number" min="1" value={months} onChange={(e) => setMonths(e.target.value)} className="mt-1 w-28" data-testid="comp-months-input" />
          </div>
        )}
        <Button onClick={grant} disabled={busy} className="ml-auto" data-testid="comp-grant-btn">
          <Gift size={16} /> {busy ? "Granting…" : "Grant plan"}
        </Button>
      </div>
    </SectionCard>
  );
}

export default function Admin() {
  const [data, setData] = useState(null);
  const [fb, setFb] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, []);

  useEffect(() => {
    load();
    api.get("/feedback").then((r) => setFb(r.data)).catch(() => {});
  }, [load]);

  const revoke = async (club) => {
    if (!window.confirm(`Revoke the comped plan for ${club.name}? They'll drop back to the Free plan.`)) return;
    try {
      await api.post("/admin/comp/revoke", { club_id: club.id });
      toast.success(`${club.name} reverted to Free`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const deleteClub = async (club) => {
    const typed = window.prompt(`This deletes "${club.name}" and ALL its data. It can be restored for ${data.delete_grace_days || 7} days, then it's gone for good. Member coaches keep their login but are detached.\n\nType the club name to confirm:`);
    if (typed === null) return;
    if (typed.trim() !== club.name) return toast.error("Name didn't match — deletion cancelled");
    try {
      await api.delete(`/admin/clubs/${club.id}`);
      toast.success(`${club.name} deleted — restorable for ${data.delete_grace_days || 7} days`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const restoreClub = async (c) => {
    try {
      await api.post(`/admin/clubs/${c.id}/restore`);
      toast.success(`${c.name} restored`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const purgeClub = async (c) => {
    if (!window.confirm(`Permanently delete "${c.name}" right now? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/clubs/${c.id}/purge`);
      toast.success(`${c.name} permanently deleted`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <Loader />;
  if (data.error) return <div className="text-center py-20 text-muted-foreground">Admin access required.</div>;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Platform Admin" subtitle="SphereHyx platform-wide analytics." testid="admin-header" />

      <div className="mb-4 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-secondary/10 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="admin-write-blog-card">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl brand-gradient text-white flex items-center justify-center shrink-0"><PenSquare size={20} /></div>
          <div>
            <p className="font-heading font-semibold">Write today's blog</p>
            <p className="text-sm text-muted-foreground mt-0.5">Publish a fresh post daily to climb Google — write your own or draft one with AI in seconds.</p>
          </div>
        </div>
        <Button asChild className="brand-gradient text-white border-0 shrink-0" data-testid="admin-write-blog-btn">
          <Link to="/platform/blog?new=1"><PenSquare size={16} className="mr-1.5" /> Write today's blog</Link>
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Building2} label="Total clubs" value={data.total_clubs} testid="admin-clubs" />
        <StatCard icon={Users} label="Coaches" value={data.total_coaches} accent="text-secondary" testid="admin-coaches" />
        <StatCard icon={UserRound} label="Athletes" value={data.total_players} testid="admin-players" />
        <StatCard icon={CreditCard} label="Active subs" value={data.active_subscriptions} accent="text-secondary" testid="admin-subs" />
        <StatCard icon={DollarSign} label="Subscription revenue" value={`$${data.monthly_revenue.toFixed(2)}`} testid="admin-revenue" />
        <StatCard icon={Percent} label="Platform fees" value={`$${data.platform_fees.toFixed(2)}`} accent="text-secondary" testid="admin-platform-fees" />
        <StatCard icon={LifeBuoy} label="Support tickets" value={data.support_tickets} testid="admin-tickets" />
      </div>

      <div className="mt-4">
        <CompCard clubs={data.clubs || []} onChange={load} />
      </div>

      <SectionCard title="Signups & subscribers" className="mt-4" testid="admin-signups">
        {data.signups?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-left"><tr>{["Coach", "Email", "Club", "Plan", "Subscription", "Joined"].map((h) => <th key={h} className="py-2 font-semibold">{h}</th>)}</tr></thead>
              <tbody>
                {data.signups.map((s) => (
                  <tr key={s.id} className="border-t border-border" data-testid={`admin-signup-${s.id}`}>
                    <td className="py-2.5 font-medium">{s.name || "—"}</td>
                    <td className="py-2.5 text-muted-foreground">{s.email}</td>
                    <td className="py-2.5">{s.club || <span className="text-muted-foreground">No club yet</span>}</td>
                    <td className="py-2.5"><span className="text-xs bg-accent px-2 py-0.5 rounded capitalize">{s.plan || "—"}</span></td>
                    <td className="py-2.5">{s.subscription_active ? <span className="text-xs font-semibold text-secondary" data-testid={`admin-sub-active-${s.id}`}>Active</span> : <span className="text-xs text-muted-foreground">Free / none</span>}</td>
                    <td className="py-2.5 text-muted-foreground">{s.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-muted-foreground">No signups yet.</p>}
      </SectionCard>

      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <SectionCard title="Plan distribution" testid="admin-plans">
          <div className="space-y-3">
            {Object.entries(data.plan_distribution || {}).map(([k, v]) => (
              <div key={k}>
                <div className="flex justify-between text-sm mb-1"><span className="capitalize">{k}</span><span className="font-semibold">{v}</span></div>
                <div className="h-2 rounded-full bg-accent overflow-hidden"><div className="h-full brand-gradient" style={{ width: `${data.total_clubs ? (v / data.total_clubs) * 100 : 0}%` }} /></div>
              </div>
            ))}
            {!Object.keys(data.plan_distribution || {}).length && <p className="text-sm text-muted-foreground">No clubs yet.</p>}
          </div>
        </SectionCard>

        <SectionCard title="Recent transactions" testid="admin-transactions">
          {data.recent_transactions?.length ? (
            <div className="divide-y divide-border">
              {data.recent_transactions.map((t) => (
                <div key={t.session_id} className="flex items-center justify-between py-2.5">
                  <div>
                    <span className="text-sm capitalize">{t.fee_type || t.kind}</span>
                    <span className="block text-xs text-muted-foreground">{t.club_name || "—"}{t.kind === "subscription" && t.club_plan ? ` · ${t.club_plan} plan` : ""}</span>
                  </div>
                  <span className="font-semibold text-sm">{(t.currency || "usd").toUpperCase()} {t.amount?.toFixed(2)}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">No transactions yet.</p>}
        </SectionCard>
      </div>

      <SectionCard title="Support & feedback" className="mt-4" testid="admin-feedback">
        {fb.length ? (
          <div className="divide-y divide-border">
            {fb.map((f) => (
              <div key={f.id} className="py-3" data-testid={`admin-feedback-${f.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${f.type === "bug" ? "bg-destructive/15 text-destructive" : f.type === "feature" ? "bg-primary/15 text-primary" : "bg-accent"}`}>{f.type}</span>
                  <span className="font-medium text-sm">{f.subject}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{f.message}</p>
                <p className="text-xs text-muted-foreground mt-1">{f.user_name} ({f.user_email}) · {f.created_at?.slice(0, 10)}</p>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground">No feedback yet.</p>}
      </SectionCard>

      <SectionCard title="Clubs" className="mt-4" testid="admin-club-list">
        {data.clubs?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-left"><tr>{["Club", "Sport", "Plan", "Country", ""].map((h) => <th key={h} className="py-2 font-semibold">{h}</th>)}</tr></thead>
              <tbody>
                {data.clubs.map((c) => (
                  <tr key={c.id} className="border-t border-border" data-testid={`admin-club-row-${c.id}`}>
                    <td className="py-2.5 font-medium">{c.name}</td>
                    <td className="py-2.5 capitalize text-muted-foreground">{c.sport?.replace("_", " ")}</td>
                    <td className="py-2.5">
                      <span className="text-xs bg-accent px-2 py-0.5 rounded capitalize">{c.plan}</span>
                      {c.comped && (
                        <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-secondary/15 text-secondary" data-testid={`admin-comp-badge-${c.id}`} title={c.comp_until ? `Until ${c.comp_until.slice(0, 10)}` : "Forever"}>
                          Comped{c.comp_until ? ` · ${c.comp_until.slice(0, 10)}` : " · ∞"}
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 text-muted-foreground">{c.country}</td>
                    <td className="py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {c.comped && (
                          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => revoke(c)} data-testid={`admin-revoke-btn-${c.id}`}>Revoke</Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => deleteClub(c)} data-testid={`admin-delete-club-btn-${c.id}`}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-muted-foreground">No clubs yet.</p>}
      </SectionCard>

      {data.deleted_clubs?.length > 0 && (
        <SectionCard title="Recently deleted" className="mt-4" testid="admin-deleted-clubs">
          <p className="text-sm text-muted-foreground mb-4">Scheduled for permanent deletion. Restore before the purge date, or delete now.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-left"><tr>{["Club", "Deleted", "Auto-purges", ""].map((h) => <th key={h} className="py-2 font-semibold">{h}</th>)}</tr></thead>
              <tbody>
                {data.deleted_clubs.map((c) => (
                  <tr key={c.id} className="border-t border-border" data-testid={`admin-deleted-row-${c.id}`}>
                    <td className="py-2.5 font-medium">{c.name}</td>
                    <td className="py-2.5 text-muted-foreground">{c.deleted_at?.slice(0, 10)}</td>
                    <td className="py-2.5 text-muted-foreground">{c.purge_at?.slice(0, 10) || "—"}</td>
                    <td className="py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" className="text-secondary hover:text-secondary" onClick={() => restoreClub(c)} data-testid={`admin-restore-btn-${c.id}`}>Restore</Button>
                        <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => purgeClub(c)} data-testid={`admin-purge-btn-${c.id}`}>Delete now</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}
    </div>
  );
}
